import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import crossIcon from '../../../assets/close.png';
import { generateClient } from 'aws-amplify/api';
import { onSlideChange } from '../../../graphql/subscriptions';
import './producer-ppt.css';
import loadingPlaceholder from '../../../assets/loading.png';
import { useParams } from 'react-router';
import CpeCredit from '../../cpe-credit/cpe-credit';
import '../../../components/cpe-credit/cpe-credit.css';

const client = generateClient();

interface Slide {
    sequence: number;
    url?: string;
    type?: string;
    pollId?: string; // Added pollId for handling poll questions
    pollResults?: PollResult[]; // Added pollResults for handling poll results
}

interface PollQuestion {
    pollId: string;
    questionText: string;
    options: Record<string, string>; // Option keys and values
}

interface PollResult {
    option: string;
    percentage: number;
    voteCount: number;
}

const PptViewer: React.FC<any> = ({ slidesData, pollData, allPollResultData }) => {
    const [isFullScreen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const { eventId } = useParams<{ eventId: string }>();

    useEffect(() => {
        if (slidesData && slidesData.length > 0) {
            const initialSlide = slidesData.find((slide: Slide) => slide.sequence === 1) || null;
            setCurrentSlide(initialSlide);
        }
    }, [slidesData]);

    useEffect(() => {
        const subscription = client.graphql({ query: onSlideChange }).subscribe({
            next: ({ data }) => {
                const newSlide = data.onSlideChange;
                if (newSlide && newSlide.id === eventId) {
                    const formattedSlide: Slide = {
                        sequence: newSlide.sequence,
                        url: newSlide.url,
                        type: newSlide.type,
                        pollId: newSlide.pollId, // Capture pollId if available
                        pollResults: newSlide.pollResults
                            ? newSlide.pollResults.filter((result: any) => result !== null).map((result: any) => ({
                                option: result.option,
                                percentage: result.percentage,
                                voteCount: result.voteCount
                            }))
                            : undefined
                    };
                    setCurrentSlide(formattedSlide);
                }
            },
            error: (error) => {
                console.error('Error with slide change subscription:', error);
            },
        });

        return () => subscription.unsubscribe();
    }, [eventId]);

    const toggleFullScreen = () => {
        const eleAssetWrapper = document.querySelector('.asset-wrapper') as HTMLElement;
        const elem = currentSlide?.type === 'video'
            ? slideContainerRef.current?.querySelector('video')
            : slideContainerRef.current;

        const enterFullScreen = () => {
            if (elem && elem.requestFullscreen) {
                eleAssetWrapper?.classList?.remove('live-view-panel');
                eleAssetWrapper?.classList?.add('live-view-panel1');
                elem.requestFullscreen().catch(err => {
                    console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                });
            }
        };

        const exitFullScreen = () => {
            eleAssetWrapper?.classList?.remove('live-view-panel1');
            eleAssetWrapper?.classList?.add('live-view-panel');
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => {
                    console.log(`Error attempting to exit full-screen mode: ${err.message} (${err.name})`);
                });
            }
        };

        if (!document.fullscreenElement) {
            enterFullScreen();
        } else {
            exitFullScreen();
        }
    };

    // Capture escape button and fullscreen change event
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                const eleAssetWrapper = document.querySelector('.asset-wrapper') as HTMLElement;
                eleAssetWrapper?.classList?.remove('live-view-panel1');
                eleAssetWrapper?.classList?.add('live-view-panel');
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const renderPollQuestion = (pollId: string) => {
        const poll = pollData.find((p: PollQuestion) => p.pollId === pollId);
        if (!poll) return null;

        return (
            <div className='right-side-ppt'>
            <div className="poll-question pollQ">
                <h3 className='right-side-question-wrapper question-text'>Q.{poll.questionTitle}</h3>
                <form className='right-side-ppt-option'>
                    {Object.entries(poll.options).map(([key, value]) => (
                        <div key={key}>
                            <input
                                type="radio"
                                id={key}
                                name={`question-${poll.pollId}`}
                                value={key}
                            />
                            <label className='option-text-wrapper' htmlFor={key}>{String(value)}</label>
                        </div>
                    ))}
                </form>
            </div>
            </div>
        );
    };

    const renderPollResults = (pollId: string) => {
        const pollResults = currentSlide?.pollResults || [];
        
        if (!pollResults.length) {
            return <div>Loading poll results...</div>;
        }

        return (
            <div className="poll-result right-side-ppt-live-view">
                <h3 className='right-side-question-wrapper'>Results</h3>
                <div className='live-view-result-show'>
                {pollResults.map(result => (
                    <div key={result.option} className="poll-result-item">
                        <div className="poll-option">{result.option}</div>
                        <div className="poll-percentage">
                            <div className="poll-bar" style={{ width: `${result.percentage}%`, backgroundColor: '#007bff' }}></div>
                            <div className="poll-text">
                                {result.percentage}% ({result.voteCount} votes)
                            </div>
                        </div>
                    </div>
                ))}
                </div>
            </div>
        );
    };

    return (
        <div className="main-ppt-wrapper">
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: "center" }} className="main-ppt-wrapper-live-view">
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'red', marginLeft: '10px' }}></span>
                    <h6 style={{ paddingTop: "10px", marginLeft: "10px" }}>Live view</h6>
                </div>
                <div>
                    <img src={crossIcon} alt="Close icon" style={{ marginRight: '10px', width: '13px', height: '13px', cursor: 'pointer', display: "none" }} />
                </div>
            </div>
            <div ref={slideContainerRef} className="position-relative slide-container" style={{ width: '100%' }}>
                <button onClick={toggleFullScreen} className="btn btn-light position-absolute end-0 m-1" style={{ bottom: '10px', right: '10px', zIndex: 10 }}>
                    <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} style={{ fontSize: '1.2rem' }} />
                </button>
                <div className='asset-wrapper live-view-panel'>
                    {currentSlide?.type === 'video' || currentSlide?.type === 'video/mp4' ? (
                        <video autoPlay style={{ width: '100%', objectFit: 'contain', pointerEvents: 'none' }}>
                            <source src={currentSlide.url} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    ) : currentSlide?.type === 'pollQuestions' && currentSlide.pollId ? (
                        renderPollQuestion(currentSlide.pollId)
                    ) : currentSlide?.type === 'pollresult' && currentSlide.pollId ? (
                        renderPollResults(currentSlide.pollId)
                    
                    ) : currentSlide?.type === 'cpeForm' ? (
                       <CpeCredit className="file2-classname" />
                    ) : (
                        <img crossOrigin="anonymous" src={currentSlide?.url || loadingPlaceholder} alt={`Slide ${currentSlide?.sequence || 'default'}`} style={{ width: '100%', objectFit: 'contain' }} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PptViewer;
